// File: src/pages/Facts.jsx
// BUCKET: Growth & Strategy
// "What's working, what to do next"

import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  ArrowRight,
  Eye,
  ArrowLeft,
  Globe,
  BarChart3,
  Zap,
  LayoutDashboard
} from 'lucide-react';
import { Spinner } from '@shopify/polaris';
import LayoutWrapper from '../components/Layout/Layout';
import { AuthContext } from '../context/AuthContext';
import './DashboardBucket.css';
import { formatPageLabel } from '../utils/cleanText';
import useData from '../hooks/useData';
import GA4SetupPrompt from '../components/GA4SetupPrompt';
import DateRangePills from '../components/ui/DateRangePills';

export default function Facts() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

  const [daysRange, setDaysRange] = useState('7');
  const handleRangeChange = useCallback((value) => setDaysRange(value), []);

  // Fetch siteId first
  const { data: siteData, loading: siteLoading, error: siteError } = useData(
    'facts:sites',
    async () => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch sites.');
      const sites = await res.json();
      if (sites.length === 0) throw new Error('No sites configured for this account.');
      sessionStorage.setItem('site_id', sites[0].id);
      return sites[0].id;
    },
    { deps: [token] }
  );

  // FIXED: Use /integrations/status endpoint
  const { data: ga4Status, loading: ga4StatusLoading, error: ga4StatusError } = useData(
    `ga4Status:${token}`,
    async () => {
      if (!token) return { connected: false };
      const res = await fetch(`${API}/integrations/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return { connected: false };
      return res.json();
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );
  const ga4Enabled = ga4Status?.connected || false;

  // Fetch metrics data
  const { data, loading, error } = useData(
    `facts:metrics:${siteData}:${daysRange}:${ga4Enabled}`,  // FIXED: use ga4Enabled instead of savedPropertyId
    async () => {
      if (!siteData) return {};
      const qs = new URLSearchParams({ site_id: siteData, days: daysRange }).toString();

      const essentialPromises = [
        fetch(`${API}/api/business/overview?${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/customer-intelligence/top-products?${qs}&limit=5`, { headers: { Authorization: `Bearer ${token}` } })
      ];
      const [overviewRes, topProductsRes] = await Promise.all(essentialPromises);
      if (!overviewRes.ok || !topProductsRes.ok) throw new Error("Failed to fetch essential data.");

      const overview = await overviewRes.json();
      const highestExit = overview?.highest_exit || overview?.highestExit;

      let pageViews = [];

      if (ga4Enabled) {
        const pageViewsRes = await fetch(`${API}/api/business/page-views?limit=5&start_date=${daysRange}daysAgo&end_date=yesterday`, { headers: { Authorization: `Bearer ${token}` } });
        if (pageViewsRes.ok) pageViews = await pageViewsRes.json();
      }

      return { highestExit, pageViews };
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [siteData, daysRange, ga4Enabled, token] }  // FIXED: use ga4Enabled
  );

  useEffect(() => {
    document.title = 'Growth & Strategy - Baobrain Analytics';
  }, []);

  const {
    highestExit = { page_path: 'N/A', exit_rate_pct: 0 },
    pageViews = []
  } = data || {};

  const totalPageViews = pageViews.reduce((sum, pv) => sum + pv.views, 0);

  // 2 widget cards for Growth & Strategy bucket
  const widgets = useMemo(() => ([
    {
      title: 'Performance Dashboard',
      description: 'Revenue trends, audience breakdown, traffic sources & strategic recommendations',
      detailsPath: '/performanceDashboard',
      icon: BarChart3,
      bgGradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    },
    {
      title: 'Social Intelligence',
      description: 'Brand mentions, product sentiment, review monitoring & competitor intel',
      detailsPath: '/customer-voice',
      icon: Globe,
      bgGradient: 'linear-gradient(135deg, #10b981, #059669)',
    },
  ]), []);

  // FIXED: use ga4StatusError instead of propertyError
  const anyError = siteError || ga4StatusError || error;

  if (!token) {
    return (
      <LayoutWrapper>
        <div className="bucket-page">
          <div className="bucket-container">
            <div className="auth-required">
              <div className="auth-icon">
                <TrendingUp size={48} />
              </div>
              <h2>Authentication Required</h2>
              <p>Please log in to access growth and strategy insights.</p>
              <button className="auth-btn" onClick={() => navigate('/login')}>
                Sign In
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  if (anyError) {
    return (
      <LayoutWrapper>
        <div className="bucket-page">
          <div className="bucket-container">
            <div className="back-button-wrapper">
              <button className="back-icon-button" onClick={() => navigate('/dashboard')}>
                <ArrowLeft size={20} />
              </button>
            </div>
            <div className="bucket-error">
              <div className="error-icon">
                <Eye size={48} />
              </div>
              <h2>Error Loading Data</h2>
              <p>{anyError.message || anyError}</p>
              {anyError.message?.includes("No sites configured") ? (
                <button className="auth-btn" onClick={() => navigate('/how-it-works')}>
                  Configure Site
                  <ArrowRight size={16} />
                </button>
              ) : (
                <button className="auth-btn" onClick={() => window.location.reload()}>
                  Retry
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  // FIXED: use ga4StatusLoading instead of propertyLoading
  if (loading || siteLoading || ga4StatusLoading) {
    return (
      <LayoutWrapper>
        <div className="bucket-page">
          <div className="bucket-container">
            <div className="back-button-wrapper">
              <button className="back-icon-button" onClick={() => navigate('/dashboard')}>
                <ArrowLeft size={20} />
              </button>
            </div>
            <div className="bucket-loading">
              <Spinner accessibilityLabel="Loading data" size="large" />
              <h2>Loading Growth Data</h2>
              <p>Fetching your strategic insights...</p>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <div className="bucket-page">
        <div className="bucket-container">
          <div className="back-button-wrapper">
            <button className="back-icon-button" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={20} />
            </button>
          </div>
          
          <div className="bucket-header">
            <div className="header-content">
              <div className="header-badge">
                <TrendingUp size={16} />
                <span>Growth & Strategy</span>
              </div>
              <h1>Growth & Strategy</h1>
              <p>What's working and what to do next. Forward-looking strategy and market position.</p>
            </div>
            
            <div className="date-range-wrapper">
              <DateRangePills value={daysRange} onChange={handleRangeChange} />
            </div>
          </div>
          
          {/* GA4 Setup Prompt - shown when GA4 not connected (only after loading) */}
          {!ga4StatusLoading && !loading && !ga4Enabled && (
            <GA4SetupPrompt variant="inline" feature="page view analytics" />
          )}
          
          {/* 2 KPI Cards - Centered */}
          <div className="quick-stats quick-stats--center-2">
            <div className="stat-item stat-item--centered">
              <Zap className="stat-icon" size={20} />
              <div className="stat-content">
                <span className="stat-value">{formatPageLabel(highestExit?.page_path) || 'N/A'}</span>
                <span className="stat-label">Highest Exit Page ({(highestExit?.exit_rate_pct ?? 0).toFixed(1)}%)</span>
              </div>
            </div>
            <div className="stat-item stat-item--centered">
              <LayoutDashboard className="stat-icon" size={20} />
              <div className="stat-content">
                <span className="stat-value">{totalPageViews.toLocaleString()}</span>
                <span className="stat-label">Total Page Views ({daysRange}D)</span>
              </div>
            </div>
          </div>
          
          <p className="focus-line">Focus: Strategic growth opportunities.</p>
          
          {/* Widget Cards */}
          <div className="widgets-grid widgets-grid--center-2">
            {widgets.map(({ title, description, detailsPath, icon: Icon, bgGradient }) => (
              <div key={title} className="widget-card">
                <div className="widget-header">
                  <div className="widget-icon" style={{ background: bgGradient }}>
                    <Icon size={24} color="white" />
                  </div>
                </div>
                <div className="widget-content">
                  <h3>{title}</h3>
                  <p className="widget-description">{description}</p>
                </div>
                <div className="widget-footer">
                  <button className="widget-btn" onClick={() => navigate(detailsPath)}>
                    Analyze Now
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
}